import api from '@/utils/api';
import workPeriod from '@/utils/workPeriod';
import workPeriodNavigation from '@/utils/workPeriodNavigation';

export default {
    mixins: [workPeriodNavigation],
    data: () => ({
        periodRouteReady: false, periodOrigin: '', periodRequestId: 0, periodTransition: false,
        selectedPeriodStreamId: null, periodMissing: false, periodLoadFailed: false, periodCreating: false,
        periodSaveCount: 0, periodSaveFailed: false, reviewRequestId: 0, streamMutationPending: false,
        periodActionConfirmation: false, periodRefreshing: false
    }),
    created() {
        this._periodSaves = new Set();
        ['submitTimesheet', 'approveTimesheet', 'assignEmployeeFromIncident',
            'undoUnresolvedMerge', 'deleteActivityComment', 'applyBulkValue', 'applyAbsenceSuggestions',
            'undoAbsenceSuggestions', 'resolveAbsenceDiscrepancy'].forEach(name => {
            const original = this[name];
            this[name] = async (...args) => {
                if (this.periodNavigationLocked) return;
                this.periodActionConfirmation = true;
                try {
                    const independentAction = name === 'deleteActivityComment' || name === 'assignEmployeeFromIncident';
                    if (!independentAction && !await this.flushTimesheetEdits()) {
                        if (name === 'submitTimesheet') this.submitActionError = 'Сначала сохраните или исправьте несохранённые отметки. Они оставлены в табеле.';
                        if (name === 'applyBulkValue') this.bulkError = 'Сначала сохраните или исправьте несохранённые отметки. Они оставлены в табеле.';
                        return;
                    }
                    return await original(...args);
                } finally { this.periodActionConfirmation = false; }
            };
        });
    },
    computed: {
        periodDraftsDirty() { return Boolean(this.editDirtyCount); },
        periodSection() { return this.reviewMode ? 'review' : this.periodOrigin || 'fill'; },
        periodNavigationLocked() {
            return Boolean(this.batchEditing || (this.hoursArrivalRequirement && this.plannedArrivalModalVisible) || this.periodGuardPending || this.handoffPending || this.submitting || this.reviewActionPending
                || this.absenceApplying || this.activityPending || this.addingPerson || this.removingLineId
                || this.plannedMovePendingId !== null || this.assigningIncidentId || this.periodCreating || this.streamMutationPending
                || this.periodActionConfirmation || this.periodRefreshing || this.rosterExclusionPending || this.unresolvedResolutionPending);
        },
        periodModeLabel() {
            return this.reviewMode ? (this.bootstrapData.permissions.can_review ? 'Проверка табелей' : 'Свод табелей') : this.periodOrigin === 'review' ? 'Табель · проверка'
                : this.periodOrigin === 'payroll' ? 'Табель · зарплата' : 'Заполнение табеля';
        },
        periodObjectLabel() {
            if (this.reviewMode) return '';
            const object = this.sheet && this.sheet.object || (this.bootstrapData.objects || [])
                .find(row => Number(row.building_id) === Number(this.buildingId));
            return [object && object.name, this.sheet && this.sheet.timesheet.name].filter(Boolean).join(' · ');
        },
        payrollPeriodLink() {
            const fromReview = this.reviewMode || this.periodOrigin;
            return {path: '/payroll', query: {month: fromReview ? this.periodMonth : this.resolvePeriod('payroll'), from: fromReview ? 'review' : 'fill'}};
        }
    },
    watch: {
        '$route.fullPath'() {
            if (this.periodRouteReady && this.$route.fullPath !== this._canonicalPeriodPath) this.loadPeriodRoute(this.$route);
        }
    },
    beforeDestroy() { this.periodRequestId += 1; this.reviewRequestId += 1; this.sheetRequestId += 1; },
    methods: {
        samePeriodRoute(route) {
            return Boolean(this.sheet && !this.reviewMode && Number(route.params.timesheetId) === Number(this.sheet.timesheet.id)
                && (Number(route.query.review_revision) || 0) === (Number(this.sheet.review_revision && this.sheet.review_revision.id) || 0));
        },
        async preparePeriodLeave() {
            if (this.periodNavigationLocked) return false;
            if (this.reviewMode) this.saveReviewView();
            this.periodGuardPending = true;
            try {
                if (typeof document !== 'undefined' && document.activeElement && this.$el.contains(document.activeElement)) document.activeElement.blur();
                await this.$nextTick();
                while (this._periodSaves.size) await Promise.all(Array.from(this._periodSaves).map(promise => promise.catch(() => false)));
                const dirty = this.periodDraftsDirty || this.periodSaveFailed || this.editErrorCount;
                const formDirty = this.returnModalVisible && this.returnReason || this.correctionModalVisible && this.correctionReason
                    || this.addPersonModalVisible && (this.addReason || this.unresolvedForm.full_name || this.unresolvedForm.phone)
                    || this.sheet && String(this.assessmentOmissionReasonDraft || '') !== String(this.sheet.timesheet.characteristic_omission_reason || '');
                const streams = this.$refs.namedTimesheets;
                const activity = this.$refs.periodActivity;
                const commentDirty = activity && (activity.newComment || activity.editingCommentId && activity.editCommentText !==
                    ((activity.items || []).find(item => item.comment_id === activity.editingCommentId) || {}).body);
                if (dirty || formDirty || commentDirty || streams && streams.hasUnsavedChanges()) {
                    const discard = await this.$bvModal.msgBoxConfirm(
                        'Есть несохранённые изменения. Можно остаться и сохранить их или перейти без сохранения.',
                        {title: 'Несохранённые изменения', okTitle: 'Перейти без сохранения', cancelTitle: 'Остаться', okVariant: 'danger'}
                    );
                    if (!discard) return false;
                }
                return true;
            } finally { this.periodGuardPending = false; }
        },
        rememberTimesheetPeriod() {
            this.rememberPeriod(this.periodSection, {
                month: this.periodMonth,
                ...(this.periodSection === 'fill' ? {object_id: this.buildingId, stream_id: this.selectedPeriodStreamId} : {})
            });
        },
        periodListRoute(month = this.periodMonth, mode = this.reviewMode ? 'review' : 'fill') {
            return {name: 'timesheets', query: {month, mode,
                ...(mode === 'fill' && month === this.$route.query.month && this.$route.query.attention === 'arrivals'
                    ? {attention: 'arrivals', ...(this.$route.query.arrival_key ? {arrival_key: this.$route.query.arrival_key} : {})} : {}),
                ...(mode === 'review' ? this.reviewQueryForMonth(month) : {}),
                ...(mode === 'fill' && this.buildingId ? {object_id: String(this.buildingId)} : {}),
                ...(mode === 'fill' && this.selectedPeriodStreamId ? {stream_id: String(this.selectedPeriodStreamId)} : {}),
                ...(mode === 'fill' && this.periodOrigin ? {from: this.periodOrigin} : {})}};
        },
        async changePeriodMonth(month) {
            if (!workPeriod.validMonth(month) || month === this.periodMonth || this.periodNavigationLocked) return;
            await this.pushPeriodRoute(this.periodListRoute(month));
        },
        async refreshPeriod() {
            if (!await this.confirmPeriodLeave()) return;
            if (this.reviewMode && this.periodSummary && !this.periodTransition) {
                this.periodRefreshing = true;
                try { await this.refreshActiveReview(); }
                finally { this.periodRefreshing = false; }
                return;
            }
            await this.loadPeriodRoute(this.$route, true);
        },
        async loadPeriodRoute(route, force = false) {
            const sheetId = Number(route.params.timesheetId) || null;
            if (!force && !sheetId && this.reviewMode && route.query.mode === 'review' && route.query.month === this.periodMonth) {
                const previousView = this.activeReviewView();
                const previousKind = this.reviewViewState.kind;
                const previousDrafts = this.reviewViewState.includeDrafts;
                this.restoreReviewView(route);
                const view = this.activeReviewView();
                if (view !== previousView || previousKind !== this.reviewViewState.kind || previousDrafts !== this.reviewViewState.includeDrafts) {
                    this._reviewViewEpoch = (this._reviewViewEpoch || 0) + 1;
                    this.reviewRequestId += 1;
                    this.reviewGridRequest += 1;
                    this.reviewLoading = false;
                    this.reviewGridLoading = false;
                }
                if (previousKind !== this.reviewViewState.kind || previousDrafts !== this.reviewViewState.includeDrafts) {
                    this.reviewGrid = null;
                    this._reviewStale.grid = true;
                }
                if ((view !== previousView && this._reviewStale[view]) || (view === 'grid' && !this.reviewGrid)) await this.loadActiveReview(view);
                await this.restoreReviewScroll();
                return;
            }
            if (!force && this.samePeriodRoute(route)) {
                this.periodOrigin = ['review', 'payroll'].includes(route.query.from) ? route.query.from : '';
                this.periodMonth = this.sheet.period.starts_on.slice(0, 7);
                this.rememberTimesheetPeriod();
                if (route.query.arrival_key) await this.loadTimesheet(sheetId, {syncRoute: false});
                await this.syncTimesheetRoute(sheetId, true);
                await this.focusReviewSource();
                await this.focusRequestedEmployee();
                await this.openRequestedPlannedArrival();
                return;
            }
            if (this.reviewMode) this.saveReviewView();
            const request = ++this.periodRequestId;
            this._reviewStale = {grid: true, readiness: true};
            this._reviewAccessEpoch = (this._reviewAccessEpoch || 0) + 1;
            this.reviewRequestId += 1;
            this.reviewGridRequest += 1;
            this.reviewGrid = null;
            this.reviewGridLoading = false;
            this.reviewFocusMessage = '';
            this.periodOrigin = ['review', 'payroll'].includes(route.query.from) ? route.query.from : '';
            this.reviewMode = !sheetId && route.query.mode === 'review' && Boolean(this.bootstrapData.permissions.can_view_review);
            this.periodMonth = this.resolvePeriod(this.periodSection, route.query.month);
            if (this.reviewMode || this.periodOrigin === 'review') this.restoreReviewView(route);
            const saved = this.savedPeriod(this.periodSection);
            const objectId = Number(route.query.object_id || saved.object_id || this.buildingId) || null;
            const streamId = Number(route.query.stream_id || saved.stream_id) || null;
            this.clearCurrentTimesheet();
            this.selectObjectById(objectId);
            this.selectedPeriodStreamId = streamId;
            this.periodSummary = null;
            this.reviewLoading = false;
            this.reviewSheets = [];
            this.periodHistoricalObjects = [];
            this.activityItems = [];
            this.periodMissing = false;
            this.periodLoadFailed = false;
            this.errorMessage = '';
            this.successMessage = '';
            this.plannedArrivalLinkMessage = '';
            this.plannedArrivalModalVisible = false;
            this.periodTransition = true;
            this.loading = true;
            this.assessmentModalVisible = false;
            this.addPersonModalVisible = false;
            this.returnModalVisible = false;
            this.correctionModalVisible = false;
            this.inspectorEmployee = null;
            this.inspectorContext = null;
            this.inspectorRequestId += 1;
            this.periodSaveFailed = false;
            try {
                if (sheetId) {
                    await this.loadTimesheet(sheetId, {syncRoute: false});
                } else if (this.reviewMode) {
                    // Summary determines whether this is a historical period.
                    // Afterwards only the visible review representation is loaded.
                    const success = await this.loadReview();
                    if (request !== this.periodRequestId) return;
                    if (success === false) this.periodLoadFailed = true;
                    else if (this.activeReviewView() === 'grid') await this.loadActiveReview('grid');
                } else {
                    let id = null;
                    const periodSheets = await api.get('/api/timesheets', {params: {period_start: `${this.periodMonth}-01`, status: 'all'}});
                    if (request !== this.periodRequestId) return;
                    this.rememberHistoricalObjects(periodSheets.data);
                    this.selectObjectById(objectId);
                    if (objectId) {
                    const sheets = periodSheets.data.filter(row => row.object && Number(row.object.building_id) === objectId && !['annulled', 'pilot_closed'].includes(row.status));
                    const historical = sheets.find(row => row.source === 'legacy_google');
                    if (historical) {
                        id = historical.id;
                    } else if (this.selectedObjectMultiple && !this.periodHistoricalObjects.length) {
                        const response = await api.get('/api/timesheets/streams', {params: {building_id: objectId, period_start: `${this.periodMonth}-01`}});
                        if (request !== this.periodRequestId) return;
                        const stream = response.data.streams.find(row => Number(row.id) === streamId)
                            || (!streamId && response.data.streams[0]);
                        this.selectedPeriodStreamId = stream ? Number(stream.id) : streamId;
                        id = stream && stream.timesheet_id;
                    } else {
                        const candidate = sheets.find(row => row.record_kind === 'official') || sheets[0];
                        id = candidate && candidate.id;
                    }
                    if (id) await this.loadTimesheet(id, {syncRoute: false});
                    else this.periodMissing = true;
                    }
                }
                if (request !== this.periodRequestId) return;
                this.rememberTimesheetPeriod();
            } catch (error) {
                if (request !== this.periodRequestId) return;
                this.clearCurrentTimesheet();
                this.periodLoadFailed = true;
                this.errorMessage = this.responseMessage(error, 'Не удалось загрузить выбранный период. Повторите загрузку.');
            } finally {
                if (request === this.periodRequestId) { this.periodTransition = false; this.loading = false; }
            }
            if (request === this.periodRequestId && !this.periodLoadFailed) {
                if (this.sheet) await this.syncTimesheetRoute(this.sheet.timesheet.id, true);
                else await this.syncPeriodRoute(this.periodListRoute());
            }
            if (request === this.periodRequestId && this.sheet) await this.focusReviewSource();
            if (request === this.periodRequestId && this.sheet) await this.focusRequestedEmployee();
            if (request === this.periodRequestId && this.reviewMode) await this.restoreReviewScroll();
        },
        async toggleReviewMode() {
            if (this.reviewMode) {
                const saved = this.savedPeriod('fill');
                await this.pushPeriodRoute({name: 'timesheets', query: {mode: 'fill', month: this.resolvePeriod('fill'),
                    ...(saved.object_id ? {object_id: String(saved.object_id)} : {}),
                    ...(saved.stream_id ? {stream_id: String(saved.stream_id)} : {})}});
            } else {
                const month = this.periodOrigin === 'review' ? this.periodMonth : this.resolvePeriod('review');
                await this.pushPeriodRoute({name: 'timesheets', query: {mode: 'review', month, ...this.reviewQueryForMonth(month)}});
            }
        },
        async selectPeriodStream(streamId) {
            const target = this.periodListRoute();
            delete target.query.arrival_key;
            if (streamId) target.query.stream_id = String(streamId);
            else delete target.query.stream_id;
            await this.pushPeriodRoute(target);
        },
        async selectPeriodObject(option) {
            if (!option || this.periodNavigationLocked) return;
            const target = this.periodListRoute();
            target.query.object_id = String(option.value);
            delete target.query.arrival_key;
            delete target.query.stream_id;
            this.objectPickerOpen = false;
            await this.pushPeriodRoute(target);
        },
        reviewSheetLink(id) {
            return this.$router.resolve({name: 'timesheet', params: {timesheetId: String(id)}, query: {from: 'review', month: this.periodMonth}}).href;
        },
        async openNamedTimesheet(id) {
            await this.pushPeriodRoute({name: 'timesheet', params: {timesheetId: String(id)}, query: {
                month: this.periodMonth, ...(this.periodOrigin ? {from: this.periodOrigin} : {})}});
        },
        async syncTimesheetRoute(id, replace = false) {
            const query = {...this.$route.query, month: this.periodMonth};
            delete query.mode;
            delete query.object_id;
            delete query.stream_id;
            if (this.periodOrigin) query.from = this.periodOrigin;
            else delete query.from;
            return this.syncPeriodRoute({name: 'timesheet', params: {timesheetId: String(id)}, query}, replace);
        }
    }
};
